import { seo } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Feather, Lightbulb, Users, Zap } from "lucide-react";
import { ReactNode } from "react";

export const Route = createFileRoute("/_web/_layout/about-us")({
  component: AboutUs,
  head: () => {
    const title = "About Inkriv | The Future of AI Writing";
    const description =
      "Learn how Inkriv is redefining writing with personalized AI tools for writers, researchers, and professionals.";
    const image =
      "https://res.cloudinary.com/dtcejk6h3/image/upload/v1747040895/og-image_ztxfrr.png";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function AboutUs() {
  return (
    <div className="min-h-screen bg-gradient-to-b pt-10 from-white to-gray-50 dark:from-gray-900 dark:to-gray-950">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-md md:text-xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-secondary-600 to-primary-500">
              Redefining Writing with AI
            </h1>
            <p className="text-sm md:text-base font-satoshi text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Inkriv is your AI-powered writing and research companion, built
              for writers, researchers, and professionals who value originality,
              clarity, and speed.
            </p>
          </motion.div>

          <div className="relative h-64 md:h-80 lg:h-96 my-12 rounded-xl overflow-hidden shadow-xl">
            <img
              src="/platform-action.png"
              alt="Inkriv platform in action"
              className="object-cover"
            />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-600 dark:text-gray-300 font-satoshi mb-4">
                Our mission is to personalize the writing process through AI
                that adapts to your style, understands your niche, and enhances
                your creativity.
              </p>
              <p className="text-gray-600 dark:text-gray-300 font-satoshi">
                We believe that AI should be a creative partner, not a
                replacement. Inkriv is designed to amplify your unique voice,
                not diminish it.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-8 rounded-xl">
              <blockquote className="text-base italic text-gray-700 dark:text-gray-200 font-satoshi">
                "Whether you're a seasoned writer or just getting started,
                Inkriv provides you with tools that feel less like software and
                more like a creative partner."
              </blockquote>
              <div className="mt-4 font-semibold">— Inkriv Team</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            How Inkriv Helps You
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Lightbulb className="h-8 w-8 text-purple-500" />}
              title="Smart Topic Suggestions"
              description="Get relevant, trending topics with editor briefs tailored to your niche and audience."
            />
            <FeatureCard
              icon={<Feather className="h-8 w-8 text-blue-500" />}
              title="Ghost Write"
              description="Generate high-quality drafts that match your writing style and voice."
            />
            <FeatureCard
              icon={<Zap className="h-8 w-8 text-indigo-500" />}
              title="Voice Transcription"
              description="Convert your spoken ideas into written text with accurate transcription."
            />
            <FeatureCard
              icon={<Users className="h-8 w-8 text-pink-500" />}
              title="Collaboration Tools"
              description="Work together with your team on documents with real-time editing and feedback."
            />
            <FeatureCard
              icon={<ArrowRight className="h-8 w-8 text-green-500" />}
              title="AI-Assisted Chat"
              description="Get help with rewriting, explaining, or improving your content through interactive chat."
            />
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 p-6 rounded-xl flex items-center justify-center">
              <Link
                to="/auth/signup"
                className="text-blue-600 dark:text-blue-400 font-medium flex items-center"
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="mb-4">{icon}</div>
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 text-sm font-satoshi">
        {description}
      </p>
    </div>
  );
}
